const SITE_URL = 'https://gripit.fit';
const LOGO_URL = 'https://gripit.fit/homePageLogo.png';

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Grip It',
  alternateName: 'GRIPIT.FIT',
  url: SITE_URL,
  logo: LOGO_URL,
  description: "India's premier online calisthenics and bodyweight strength training platform. Expert-coached workouts for women, men, and beginners.",
  foundingDate: '2021',
  areaServed: {
    '@type': 'Country',
    name: 'India',
  },
  knowsAbout: ['Calisthenics', 'Bodyweight Training', 'Women\'s Fitness', 'Pre-natal Fitness', 'Post-natal Fitness', 'Online Workout Classes'],
  founder: {
    '@type': 'Person',
    name: 'Srushti Shah',
    url: 'https://www.instagram.com/ssrushti',
    jobTitle: 'Certified Calisthenics Coach',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'gripitbysrushti@gmail.com',
    contactType: 'customer service',
    areaServed: 'IN',
    availableLanguage: ['English', 'Hindi'],
  },
  sameAs: [
    'https://www.instagram.com/ssrushti',
    'https://www.instagram.com/gripit.fit',
    'https://www.youtube.com/@ssrushti/',
    'https://twitter.com/srushtishah2',
  ],
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'SH4, Ground floor, Galaxy Arcade, Mahatma Gandhi Road, Vile Parle (East)',
    addressLocality: 'Mumbai',
    postalCode: '400057',
    addressRegion: 'Maharashtra',
    addressCountry: 'IN',
  },
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Grip It',
  url: SITE_URL,
  description: "India's premier online calisthenics and bodyweight strength training platform.",
  publisher: {
    '@type': 'Organization',
    name: 'Grip It',
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/blogs?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export const createPersonSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'Srushti Shah',
  jobTitle: 'Certified Calisthenics Coach & Founder',
  url: SITE_URL + '/about-us',
  image: LOGO_URL,
  worksFor: {
    '@type': 'Organization',
    name: 'Grip It',
    url: SITE_URL,
  },
  knowsAbout: [
    'Calisthenics',
    'Bodyweight Training',
    'Women\'s Fitness',
    'Pre-natal Fitness',
    'Post-natal Fitness',
    'Strength Training',
    'Online Coaching',
  ],
  sameAs: [
    'https://www.instagram.com/ssrushti',
    'https://www.youtube.com/@ssrushti/',
    'https://twitter.com/srushtishah2',
  ],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Mumbai',
    addressRegion: 'Maharashtra',
    addressCountry: 'IN',
  },
});

export const createLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SportsActivityLocation',
  name: 'Grip It — Calisthenics & Bodyweight Training',
  url: SITE_URL,
  logo: LOGO_URL,
  image: LOGO_URL,
  description: "India's premier online calisthenics platform offering expert-coached bodyweight workouts for women, men, and beginners.",
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'SH4, Ground floor, Galaxy Arcade, Mahatma Gandhi Road, Vile Parle (East)',
    addressLocality: 'Mumbai',
    postalCode: '400057',
    addressRegion: 'Maharashtra',
    addressCountry: 'IN',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 19.0760,
    longitude: 72.8777,
  },
  email: 'gripitbysrushti@gmail.com',
});

export const createExercisePlanSchema = ({ name, description, image, duration, price, currency = 'INR' }) => ({
  '@context': 'https://schema.org',
  '@type': 'ExercisePlan',
  name,
  description,
  image,
  exerciseType: 'Calisthenics / Bodyweight Training',
  activityDuration: duration || 'P30D',
  provider: {
    '@type': 'Organization',
    name: 'Grip It',
    url: SITE_URL,
  },
  ...(price && {
    offers: {
      '@type': 'Offer',
      price,
      priceCurrency: currency,
      availability: 'https://schema.org/InStock',
    },
  }),
});

export const createItemListSchema = (items, listName) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: listName,
  numberOfItems: items.length,
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    url: item.url ? `${SITE_URL}${item.url}` : undefined,
    description: item.description,
  })),
});

export const createHowToSchema = ({ name, description, steps, totalTime }) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name,
  description,
  totalTime: totalTime || 'PT30M',
  step: steps.map((step, index) => ({
    '@type': 'HowToStep',
    position: index + 1,
    name: step.name,
    text: step.text,
  })),
});

export const createVideoObjectSchema = ({ name, description, thumbnailUrl, uploadDate, embedUrl, duration }) => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name,
  description,
  thumbnailUrl,
  uploadDate,
  embedUrl,
  duration: duration || 'PT1M',
  publisher: {
    '@type': 'Organization',
    name: 'Grip It',
    logo: {
      '@type': 'ImageObject',
      url: LOGO_URL,
    },
  },
});

export const createEventSchema = ({ name, description, startDate, endDate, location, price, currency = 'INR' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Event',
  name,
  description,
  startDate,
  endDate,
  eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
  eventStatus: 'https://schema.org/EventScheduled',
  location: {
    '@type': 'VirtualLocation',
    url: location || SITE_URL,
  },
  organizer: {
    '@type': 'Organization',
    name: 'Grip It',
    url: SITE_URL,
  },
  ...(price && {
    offers: {
      '@type': 'Offer',
      price,
      priceCurrency: currency,
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}/group-calisthenics`,
    },
  }),
});

export const createProductSchema = ({ name, description, image, price, currency = 'INR' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  description,
  image,
  offers: {
    '@type': 'Offer',
    price,
    priceCurrency: currency,
    availability: 'https://schema.org/InStock',
    seller: {
      '@type': 'Organization',
      name: 'Grip It',
    },
  },
});

export const createCourseSchema = ({ name, description, price, currency = 'INR', image }) => ({
  '@context': 'https://schema.org',
  '@type': 'Course',
  name,
  description,
  image,
  provider: {
    '@type': 'Organization',
    name: 'Grip It',
    url: SITE_URL,
  },
  offers: price
    ? {
        '@type': 'Offer',
        price,
        priceCurrency: currency,
        availability: 'https://schema.org/InStock',
      }
    : undefined,
});

export const createBlogPostSchema = ({ headline, description, image, datePublished, dateModified, author = 'Srushti Shah', url }) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline,
  description,
  image,
  datePublished,
  dateModified: dateModified || datePublished,
  author: {
    '@type': 'Person',
    name: author,
    url: `${SITE_URL}/about-us`,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Grip It',
    logo: {
      '@type': 'ImageObject',
      url: LOGO_URL,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': url,
  },
});

export const createFAQSchema = (faqItems) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
    },
  })),
});

export const createBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url ? `${SITE_URL}${item.url}` : undefined,
  })),
});
